import { useEffect, useState } from "react";
import { api, formatZAR, formatInt, formatApiError } from "@/lib/api";
import { KpiCard, PageHeader } from "@/components/Kpi";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CalendarRange } from "lucide-react";

const PERIODS = [
    { label: "7d", days: 7 },
    { label: "30d", days: 30 },
    { label: "90d", days: 90 },
    { label: "365d", days: 365 },
    { label: "All", days: 3650 },
];

function formatKg(v) {
    if (v == null || isNaN(v)) return "—";
    return `${Number(v).toLocaleString("en-ZA", { maximumFractionDigits: 1 })} kg`;
}

const SERIES = [
    { key: "revenue", label: "Revenue", color: "#0079c2", axis: "l", unit: "ZAR" },
    { key: "net", label: "Net profit", color: "#e35a00", axis: "l", unit: "ZAR" },
    { key: "kg", label: "Kg", color: "#6b4423", axis: "r", unit: "kg" },
    { key: "fees", label: "Fees", color: "#b8761d", axis: "l", unit: "ZAR" },
    { key: "units", label: "Units", color: "#2f7a4d", axis: "r", unit: "u" },
];

export default function Overview() {
    const [days, setDays] = useState(30);
    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState([]);
    const [topSkus, setTopSkus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPrev, setShowPrev] = useState(false);
    const [visible, setVisible] = useState({ revenue: true, net: true, kg: true, fees: false, units: false });

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        Promise.all([
            api.get(`/dashboard/overview?days=${days}`),
            api.get(`/dashboard/sales-trend?days=${days}&include_previous=${showPrev}`),
            api.get(`/dashboard/top-skus?days=${days}&limit=8`),
        ])
            .then(([o, t, s]) => {
                if (cancel) return;
                setOverview(o.data);
                setTrend(t.data.items || []);
                setTopSkus(s.data.items || []);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => !cancel && setLoading(false));
        return () => { cancel = true; };
    }, [days, showPrev]);

    const cur = overview?.current || {};
    const ch = overview?.changes || {};
    const balances = overview?.balances || {};

    const toggleSeries = (key) => setVisible((v) => ({ ...v, [key]: !v[key] }));

    return (
        <div>
            <PageHeader title="Overview" subtitle={`Last ${days >= 3650 ? "all-time" : days + " days"} · all figures in South African Rand`}>
                <div className="inline-flex border border-[color:var(--bl-border)] rounded-sm bg-[color:var(--bl-surface)]" data-testid="period-toggle">
                    {PERIODS.map((p) => (
                        <button key={p.days} data-testid={`period-${p.label}`} onClick={() => setDays(p.days)}
                            className={`px-3 py-1.5 text-xs uppercase tracking-wider transition ${
                                days === p.days ? "bg-[color:var(--bl-takealot-blue)] text-white" : "text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)]"
                            }`}>{p.label}</button>
                    ))}
                </div>
            </PageHeader>

            {loading && !overview && (<div className="text-[color:var(--bl-text-dim)] text-sm">Loading metrics…</div>)}

            {overview && cur.revenue === 0 && cur.units === 0 && (
                <div data-testid="empty-state-banner" className="mb-6 border border-[color:var(--bl-takealot-orange)]/40 bg-[color:var(--bl-takealot-orange)]/10 text-[color:var(--bl-text)] px-4 py-3 rounded-sm text-sm">
                    No sales data yet. Press <span className="font-semibold">Sync Takealot</span> in the sidebar to pull your latest data.
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-3">
                <KpiCard label="Revenue" value={formatZAR(cur.revenue)} delta={ch.revenue_pct} sub="vs prev period" testid="kpi-revenue" />
                <KpiCard label="Units sold" value={formatInt(cur.units)} delta={ch.units_pct} sub={`${formatInt(cur.orders)} orders`} accent="orange" testid="kpi-units" />
                <KpiCard label="Kg sold" value={formatKg(cur.kg)} delta={ch.kg_pct} sub="based on kg/unit" accent="coffee" testid="kpi-kg" />
                <KpiCard label="Takealot fees" value={formatZAR(cur.fees)} delta={ch.fees_pct} sub={`COGS ${formatZAR(cur.cogs)}`} accent="orange" testid="kpi-fees" />
                <KpiCard label="Net profit" value={formatZAR(cur.net_profit)} delta={ch.net_profit_pct} sub="rev − fees − COGS" accent="green" testid="kpi-net" />
            </div>

            {overview?.status_breakdown && overview.status_breakdown.length > 1 && (
                <div data-testid="status-breakdown" className="mb-6 px-3 py-2 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm text-xs text-[color:var(--bl-text-dim)] flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="font-medium text-[color:var(--bl-text)] uppercase tracking-wider text-[10px]">Counted as revenue:</span>
                    {overview.status_breakdown.map((s) => {
                        const isCounted = s.status === "Shipped to Customer";
                        return (
                            <span key={s.status} className={`inline-flex items-center gap-1.5 ${isCounted ? "text-[color:var(--bl-success)]" : "text-[color:var(--bl-text-dim)] line-through"}`}>
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${isCounted ? "bg-[color:var(--bl-success)]" : "bg-[color:var(--bl-text-mute)]"}`} />
                                {s.status}: <span className="font-mono-num">{formatZAR(s.revenue)}</span> ({s.rows})
                            </span>
                        );
                    })}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
                <KpiCard label="Available balance" value={formatZAR(balances.available)} sub="ready to disburse" testid="kpi-balance-available" />
                <KpiCard label="Held back" value={formatZAR(balances.held_back)} sub="pending" accent="orange" testid="kpi-balance-held" />
                <KpiCard label="Current balance" value={formatZAR(balances.current)} sub="total" accent="green" testid="kpi-balance-current" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div data-testid="trend-chart" className="lg:col-span-2 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                            <div className="font-serif-display text-xl">Daily revenue, net & kg</div>
                            <div className="text-xs text-[color:var(--bl-text-dim)]">
                                Last {days} days · click a series in the legend to toggle
                            </div>
                        </div>
                        <button
                            data-testid="prev-period-toggle"
                            onClick={() => setShowPrev((v) => !v)}
                            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border transition ${
                                showPrev
                                    ? "border-[color:var(--bl-takealot-orange)] bg-[color:var(--bl-takealot-orange)] text-white"
                                    : "border-[color:var(--bl-border)] text-[color:var(--bl-text-dim)] hover:bg-[color:var(--bl-surface-2)]"
                            }`}>
                            <CalendarRange className="w-3.5 h-3.5" /> Previous period {showPrev ? "ON" : "OFF"}
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="chart-legend">
                        {SERIES.map((s) => (
                            <button
                                key={s.key}
                                data-testid={`legend-${s.key}`}
                                onClick={() => toggleSeries(s.key)}
                                className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm border transition ${
                                    visible[s.key]
                                        ? "border-[color:var(--bl-border-strong)] bg-[color:var(--bl-surface-2)] text-[color:var(--bl-text)]"
                                        : "border-[color:var(--bl-border)] text-[color:var(--bl-text-mute)] line-through"
                                }`}>
                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                                {s.label}
                            </button>
                        ))}
                        {showPrev && (
                            <span className="text-[10px] text-[color:var(--bl-text-dim)] ml-2 inline-flex items-center gap-1">
                                <span className="inline-block w-4 border-t border-dashed border-[color:var(--bl-text-dim)]" />
                                dashed = previous period
                            </span>
                        )}
                    </div>

                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trend}>
                                <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                                <XAxis dataKey="date" stroke="#75614f" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                                <YAxis yAxisId="l" stroke="#75614f" fontSize={10} tickFormatter={(v) => `R${Math.round(v / 1000)}k`} />
                                <YAxis yAxisId="r" orientation="right" stroke="#6b4423" fontSize={10} />
                                <Tooltip
                                    contentStyle={{ background: "#fffaf2", border: "1px solid #d8c8ad", fontSize: 12, color: "#2b1d15" }}
                                    formatter={(v, n) => {
                                        if (n.includes("Kg") || n.includes("Units")) return [Number(v).toFixed(n.includes("Kg") ? 1 : 0), n];
                                        return [formatZAR(v), n];
                                    }}
                                    labelFormatter={(label, payload) => {
                                        if (!payload || !payload[0]) return label;
                                        const d = payload[0].payload;
                                        return showPrev && d.prev_date ? `${label}  (prev: ${d.prev_date})` : label;
                                    }}
                                />
                                {SERIES.map((s) => (
                                    <Line key={s.key} yAxisId={s.axis} dataKey={s.key} name={s.label} stroke={s.color}
                                        strokeWidth={2} dot={false} hide={!visible[s.key]} />
                                ))}
                                {showPrev && SERIES.map((s) => (
                                    <Line key={`prev_${s.key}`} yAxisId={s.axis} dataKey={`prev_${s.key}`} name={`${s.label} (prev)`}
                                        stroke={s.color} strokeWidth={1.5} dot={false} strokeOpacity={0.55} strokeDasharray="5 4" hide={!visible[s.key]} />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div data-testid="top-skus-card" className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm">
                    <div className="font-serif-display text-xl mb-3">Top performers</div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topSkus.map(s => ({ ...s, short: (s.title || s.sku).slice(0, 18) }))} layout="vertical">
                                <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" horizontal={false} />
                                <XAxis type="number" stroke="#75614f" fontSize={10} tickFormatter={(v) => `R${Math.round(v / 1000)}k`} />
                                <YAxis type="category" dataKey="short" stroke="#75614f" fontSize={10} width={110} />
                                <Tooltip contentStyle={{ background: "#fffaf2", border: "1px solid #d8c8ad", fontSize: 12, color: "#2b1d15" }} formatter={(v) => formatZAR(v)} />
                                <Bar dataKey="revenue" fill="#0079c2" radius={[0, 2, 2, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="mt-6 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm">
                <div className="px-5 py-3 border-b border-[color:var(--bl-border)] flex items-center justify-between">
                    <div className="font-serif-display text-xl">Top products by revenue</div>
                    <Link to="/profitability" className="text-xs text-[color:var(--bl-takealot-orange)] hover:underline" data-testid="goto-profitability-link">
                        See full profitability →
                    </Link>
                </div>
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                        <tr>
                            <th className="text-left px-5 py-2">Product</th>
                            <th className="text-right px-5 py-2">Units</th>
                            <th className="text-right px-5 py-2">Kg</th>
                            <th className="text-right px-5 py-2">Revenue</th>
                            <th className="text-right px-5 py-2">Fees</th>
                            <th className="text-right px-5 py-2">Net</th>
                        </tr>
                    </thead>
                    <tbody data-testid="top-skus-table">
                        {topSkus.length === 0 && (<tr><td colSpan={6} className="text-center text-[color:var(--bl-text-dim)] py-6">No sales in this period.</td></tr>)}
                        {topSkus.map((s) => (
                            <tr key={s.sku} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]">
                                <td className="px-5 py-2">
                                    <Link to={`/products/${encodeURIComponent(s.sku)}`} className="hover:underline">
                                        <div className="text-[color:var(--bl-text)]">{s.title}</div>
                                        <div className="font-mono-num text-[10px] text-[color:var(--bl-text-dim)]">{s.sku}</div>
                                    </Link>
                                </td>
                                <td className="px-5 py-2 text-right font-mono-num">{formatInt(s.units)}</td>
                                <td className="px-5 py-2 text-right font-mono-num text-[color:var(--bl-accent-coffee)]">{formatKg(s.kg)}</td>
                                <td className="px-5 py-2 text-right font-mono-num">{formatZAR(s.revenue)}</td>
                                <td className="px-5 py-2 text-right font-mono-num text-[color:var(--bl-takealot-orange)]">{formatZAR(s.fees)}</td>
                                <td className="px-5 py-2 text-right font-mono-num text-[color:var(--bl-success)]">{formatZAR(s.net)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
